import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Search, Filter, TrendingUp, TrendingDown, X } from 'lucide-react'

interface Order {
  id: string
  user_id: string
  project_id: string
  side: 'buy' | 'sell'
  quantity_tons: number
  price_per_ton: number
  filled_quantity: number
  status: 'open' | 'filled' | 'partially_filled' | 'cancelled'
  created_at: string
}

interface Trade {
  id: string
  buy_order_id: string
  sell_order_id: string
  project_id: string
  buyer_id: string
  seller_id: string
  quantity_tons: number
  price_per_ton: number
  buyer_fee: number
  seller_fee: number
  executed_at: string
}

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [trades, setTrades] = useState<Trade[]>([])
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [filteredTrades, setFilteredTrades] = useState<Trade[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'orders' | 'trades'>('orders')
  const [searchTerm, setSearchTerm] = useState('')
  const [sideFilter, setSideFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (activeTab === 'orders') {
      filterOrders()
    } else {
      filterTrades()
    }
  }, [orders, trades, searchTerm, sideFilter, statusFilter, activeTab])

  const fetchData = async () => {
    try {
      // Use admin RPC functions to bypass RLS
      const [ordersData, tradesData] = await Promise.all([
        supabase.rpc('admin_get_all_orders'),
        supabase.rpc('admin_get_all_trades'),
      ])

      if (ordersData.error) throw ordersData.error
      if (tradesData.error) throw tradesData.error

      setOrders(ordersData.data || [])
      setTrades(tradesData.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterOrders = () => {
    let filtered = orders

    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.project_id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (sideFilter !== 'all') {
      filtered = filtered.filter((order) => order.side === sideFilter)
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((order) => order.status === statusFilter)
    }

    setFilteredOrders(filtered)
  }

  const filterTrades = () => {
    let filtered = trades

    if (searchTerm) {
      filtered = filtered.filter(
        (trade) =>
          trade.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trade.project_id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredTrades(filtered)
  }

  const cancelOrder = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', orderId)

      if (error) throw error

      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, status: 'cancelled' as const } : order
        )
      )
      setSelectedOrder(null)
    } catch (error) {
      console.error('Error cancelling order:', error)
      alert('Failed to cancel order')
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      open: 'admin-badge-info',
      filled: 'admin-badge-success',
      partially_filled: 'admin-badge-warning',
      cancelled: 'admin-badge-danger',
    }
    return badges[status as keyof typeof badges] || 'admin-badge'
  }

  const calculateTotalVolume = () => {
    return trades.reduce((sum, trade) => sum + trade.quantity_tons, 0)
  }

  const calculateTotalFees = () => {
    return trades.reduce((sum, trade) => sum + trade.buyer_fee + trade.seller_fee, 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Orders & Trades</h1>
        <p className="text-gray-600 mt-1">Monitor platform trading activity</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Open Orders</p>
          <p className="text-2xl font-bold">
            {orders.filter((o) => o.status === 'open').length}
          </p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Total Trades</p>
          <p className="text-2xl font-bold">{trades.length}</p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Total Volume</p>
          <p className="text-2xl font-bold">{calculateTotalVolume().toLocaleString()} tons</p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Total Fees Collected</p>
          <p className="text-2xl font-bold">${calculateTotalFees().toLocaleString()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'orders'
                ? 'border-admin-primary text-admin-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Orders ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('trades')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'trades'
                ? 'border-admin-primary text-admin-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Trades ({trades.length})
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="admin-card mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by ID or project..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
            />
          </div>
          {activeTab === 'orders' && (
            <>
              <div className="flex items-center gap-2">
                <Filter className="text-gray-400 w-5 h-5" />
                <select
                  value={sideFilter}
                  onChange={(e) => setSideFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
                >
                  <option value="all">All Sides</option>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="filled">Filled</option>
                  <option value="partially_filled">Partially Filled</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Orders Table */}
      {activeTab === 'orders' && (
        <div className="admin-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Side</th>
                  <th>Quantity</th>
                  <th>Price/Ton</th>
                  <th>Filled</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No orders found
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="font-mono text-xs">{order.id.slice(0, 8)}...</td>
                      <td>
                        <div className="flex items-center gap-1">
                          {order.side === 'buy' ? (
                            <TrendingUp className="w-4 h-4 text-green-600" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-600" />
                          )}
                          <span className={order.side === 'buy' ? 'text-green-600' : 'text-red-600'}>
                            {order.side.toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td>{order.quantity_tons} tons</td>
                      <td>${order.price_per_ton}</td>
                      <td>
                        {order.filled_quantity} / {order.quantity_tons}
                      </td>
                      <td>
                        <span className={getStatusBadge(order.status)}>
                          {order.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>{new Date(order.created_at).toLocaleString()}</td>
                      <td>
                        {order.status === 'open' && (
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="text-red-600 hover:text-red-700"
                            title="Cancel Order"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Trades Table */}
      {activeTab === 'trades' && (
        <div className="admin-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Trade ID</th>
                  <th>Buyer</th>
                  <th>Seller</th>
                  <th>Quantity</th>
                  <th>Price/Ton</th>
                  <th>Total</th>
                  <th>Fees</th>
                  <th>Executed</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrades.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No trades found
                    </td>
                  </tr>
                ) : (
                  filteredTrades.map((trade) => (
                    <tr key={trade.id}>
                      <td className="font-mono text-xs">{trade.id.slice(0, 8)}...</td>
                      <td className="font-mono text-xs">{trade.buyer_id.slice(0, 8)}...</td>
                      <td className="font-mono text-xs">{trade.seller_id.slice(0, 8)}...</td>
                      <td>{trade.quantity_tons} tons</td>
                      <td>${trade.price_per_ton}</td>
                      <td className="font-medium">
                        ${(trade.quantity_tons * trade.price_per_ton).toLocaleString()}
                      </td>
                      <td>${(trade.buyer_fee + trade.seller_fee).toFixed(2)}</td>
                      <td>{new Date(trade.executed_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Cancel Order</h3>
            <p className="text-gray-600 text-sm mb-4">
              Are you sure you want to cancel this order?
            </p>

            <div className="bg-gray-50 rounded-md p-4 mb-6 text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Side:</span>
                <span className="font-medium">{selectedOrder.side.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Quantity:</span>
                <span className="font-medium">{selectedOrder.quantity_tons} tons</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Price:</span>
                <span className="font-medium">${selectedOrder.price_per_ton}/ton</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Filled:</span>
                <span className="font-medium">
                  {selectedOrder.filled_quantity} / {selectedOrder.quantity_tons}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setSelectedOrder(null)}
                className="flex-1 admin-button-secondary"
              >
                Keep Order
              </button>
              <button
                onClick={() => cancelOrder(selectedOrder.id)}
                className="flex-1 admin-button-danger"
              >
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
